import { TextField } from '@habitech/keychain-atoms-inputs';
import { Button, DropdownButton } from '@habitech/call-to-action';
import { RadioButton } from '@habitech/selectors';
import type { FormikProps } from 'formik';
import Modal from 'src/components/common/modal/Modal';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import es from 'date-fns/locale/es';
import type { ProductAssignmentSubmitPayload, UserProductAssignResponse } from 'src/models/ProductAssignment';
import {
    DURATION_PRESET_OPTIONS,
    ENTRY_TYPE_OPTIONS,
    PRODUCT_8_DISPLAY_NAME,
    PRODUCT_ID_WITH_ENTRY_RULES,
    PRODUCT_OPTIONS,
    type FormValues,
} from './formConstants';
import { useProductAssignmentForm } from '../hooks/useProductAssignmentForm';
import { AssignmentSuccessSummary } from '../AssignmentSuccessSummary';
import {
    DatePickerField,
    DurationBlock,
    FieldBlock,
    FieldError,
    FieldLabel,
    FormActions,
    FormDivider,
    FormPanel,
    FormStack,
    HelperText,
    InsetPanel,
    LockedField,
    RadioGroup,
    RequiredBar,
    SectionDescription,
    SectionHeading,
    VigenciaGrid,
} from './styles';

type ShowFieldErrorFn = (field: keyof FormValues) => boolean;

type ResultModalState = {
    open: boolean;
    requestPayload: ProductAssignmentSubmitPayload | null;
    response: UserProductAssignResponse | null;
    error: string | null;
};

function JsonPayloadPre({ value, maxHeight }: { value: unknown; maxHeight: string }) {
    return (
        <pre
            style={{
                margin: 0,
                padding: '12px 14px',
                background: '#f5f5f5',
                borderRadius: 8,
                fontSize: 13,
                lineHeight: 1.45,
                overflow: 'auto',
                maxHeight,
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-word',
            }}
        >
            {JSON.stringify(value, null, 2)}
        </pre>
    );
}

function ModalErrorContent({
    error,
    requestPayload,
}: {
    error: string;
    requestPayload: ProductAssignmentSubmitPayload | null;
}) {
    return (
        <>
            <p style={{ margin: '0 16px 8px', fontWeight: 600, color: '#b71c1c' }}>No se pudo completar el envío</p>
            <p style={{ margin: '0 16px 12px' }}>{error}</p>
            {requestPayload ? (
                <>
                    <p style={{ margin: '0 16px 8px', fontSize: 13, color: '#666' }}>Cuerpo enviado</p>
                    <JsonPayloadPre value={requestPayload} maxHeight="min(40vh, 280px)" />
                </>
            ) : null}
        </>
    );
}

/** Contenido secundario del modal de éxito: resumen legible o JSON del payload. */
function ModalSuccessDetail({
    response,
    requestPayload,
}: {
    response: UserProductAssignResponse | null;
    requestPayload: ProductAssignmentSubmitPayload | null;
}) {
    if (response) {
        return <AssignmentSuccessSummary response={response} />;
    }
    if (requestPayload) {
        return <JsonPayloadPre value={requestPayload} maxHeight="min(60vh, 420px)" />;
    }
    return null;
}

function ModalSuccessContent({
    response,
    requestPayload,
}: {
    response: UserProductAssignResponse | null;
    requestPayload: ProductAssignmentSubmitPayload | null;
}) {
    return (
        <>
            <p style={{ margin: '0 0 12px', fontWeight: 600, color: '#1b5e20', fontSize: '22px' }}>Asignación registrada correctamente</p>
            <ModalSuccessDetail response={response} requestPayload={requestPayload} />
        </>
    );
}

function ProductAssignmentResultModal({
    state,
    onClose,
}: {
    state: ResultModalState;
    onClose: () => void;
}) {
    const hasContent =
        state.open &&
        (state.requestPayload != null || state.response != null || state.error != null);
    if (!hasContent) {
        return null;
    }

    const body = state.error ? (
        <ModalErrorContent error={state.error} requestPayload={state.requestPayload} />
    ) : (
        <ModalSuccessContent response={state.response} requestPayload={state.requestPayload} />
    );

    return (
        <Modal onClose={onClose}>
            {body}
            <div style={{ marginTop: 16 }}>
                <Button dataId="product-assignment-modal-close" originType="pcom" onClick={onClose}>
                    Cerrar
                </Button>
            </div>
        </Modal>
    );
}

function ProductEmailPanel({ formik, showFieldError }: { formik: FormikProps<FormValues>; showFieldError: ShowFieldErrorFn }) {
    return (
        <FormPanel aria-label="Producto y correo del usuario">
            <RequiredBar>
                Los campos visibles son obligatorios. El asterisco (*) indica dato requerido en la sección
                correspondiente.
            </RequiredBar>
            <FieldBlock>
                <FieldLabel>Correo del usuario *</FieldLabel>
                <HelperText>Cuenta a la que se asignará el producto.</HelperText>
                <TextField
                    dataId="product-assignment-email"
                    name="email"
                    label="Correo electrónico"
                    originType="pcom"
                    placeHolder="nombre@ejemplo.com"
                    innerLabel
                    type="email"
                    value={formik.values.email}
                    onChange={(_, value) => formik.setFieldValue('email', value, false)}
                    error={showFieldError('email')}
                    errorMessage={formik.errors.email ? `${formik.errors.email}` : ''}
                    onBlur={() => formik.handleBlur('email')}
                />
            </FieldBlock>
            <FormDivider />
            <FieldBlock>
                <FieldLabel>Producto *</FieldLabel>
                <HelperText>Catálogo de referencia; por default {PRODUCT_8_DISPLAY_NAME}.</HelperText>
                <LockedField>
                    <DropdownButton
                        dataId="product-assignment-product"
                        label=""
                        listOptions={PRODUCT_OPTIONS}
                        placeholder={PRODUCT_8_DISPLAY_NAME}
                        setValue={() => undefined}
                        variant="white"
                        originType="pcom"
                        size="large fluid"
                        value={formik.values.product_id}
                    />
                </LockedField>
                {showFieldError('product_id') && formik.errors.product_id ? (
                    <FieldError>{formik.errors.product_id}</FieldError>
                ) : null}
            </FieldBlock>
        </FormPanel>
    );
}

function VigenciaPanel({
    formik,
    showFieldError,
    handleDurationPresetChange,
    handleDateStartChange,
    handleDateEndChange,
}: {
    formik: FormikProps<FormValues>;
    showFieldError: ShowFieldErrorFn;
    handleDurationPresetChange: (value: string) => void;
    handleDateStartChange: (date: Date | null) => void;
    handleDateEndChange: (date: Date | null) => void;
}) {
    return (
        <FormPanel aria-labelledby="product-assignment-section-vigencia">
            <SectionHeading id="product-assignment-section-vigencia">Vigencia *</SectionHeading>
            <SectionDescription>
                Define el periodo activo. Con 1, 3 o 6 meses la fecha de fin se calcula sola; si ajustas la fin a mano y
                no coincide con esos periodos, la duración pasará a «Otra».
            </SectionDescription>

            <VigenciaGrid>
                <DatePickerField>
                    <DatePicker
                        label="Fecha de inicio"
                        format="dd/MM/yyyy"
                        value={formik.values.date_start}
                        onChange={handleDateStartChange}
                        slotProps={{
                            textField: {
                                size: 'small',
                                fullWidth: true,
                                error: showFieldError('date_start'),
                                helperText: showFieldError('date_start') ? `${formik.errors.date_start}` : '',
                                onBlur: () => formik.handleBlur('date_start'),
                            },
                        }}
                    />
                </DatePickerField>

                <DatePickerField>
                    <DatePicker
                        label="Fecha de fin"
                        format="dd/MM/yyyy"
                        value={formik.values.date_end}
                        minDate={formik.values.date_start}
                        onChange={handleDateEndChange}
                        slotProps={{
                            textField: {
                                size: 'small',
                                fullWidth: true,
                                error: showFieldError('date_end'),
                                helperText: showFieldError('date_end') ? `${formik.errors.date_end}` : '',
                                onBlur: () => formik.handleBlur('date_end'),
                            },
                        }}
                    />
                </DatePickerField>

                <DurationBlock>
                    <FieldLabel>Duración *</FieldLabel>
                    <HelperText>Por defecto 1 mes desde la fecha de inicio.</HelperText>
                    <RadioGroup>
                        <RadioButton
                            dataId="product-assignment-duration"
                            id="duration_preset"
                            name="duration_preset"
                            onChange={(value: string) => handleDurationPresetChange(value)}
                            originType="pcom"
                            value={formik.values.duration_preset}
                            radioBtnList={DURATION_PRESET_OPTIONS.map((o) => ({
                                label: o.label,
                                value: o.value,
                            }))}
                            selected={formik.values.duration_preset}
                        />
                    </RadioGroup>
                    {showFieldError('duration_preset') && formik.errors.duration_preset ? (
                        <FieldError>{formik.errors.duration_preset}</FieldError>
                    ) : null}
                </DurationBlock>
            </VigenciaGrid>
        </FormPanel>
    );
}

function ProductEntryRulesPanel({
    formik,
    showFieldError,
    handleEntryTypeChange,
}: {
    formik: FormikProps<FormValues>;
    showFieldError: ShowFieldErrorFn;
    handleEntryTypeChange: (value: string) => void;
}) {
    if (formik.values.product_id !== PRODUCT_ID_WITH_ENTRY_RULES) {
        return null;
    }

    return (
        <FormPanel aria-labelledby="product-assignment-section-entry">
            <SectionHeading id="product-assignment-section-entry">
                Tipo de ingreso ({PRODUCT_8_DISPLAY_NAME}) *
            </SectionHeading>
            <SectionDescription>Solo aplica a {PRODUCT_8_DISPLAY_NAME}.</SectionDescription>

            <FieldBlock>
                <RadioGroup>
                    <RadioButton
                        dataId="product-assignment-entry-type"
                        id="entry_type"
                        name="entry_type"
                        onChange={(value: string) => handleEntryTypeChange(value)}
                        originType="pcom"
                        value={formik.values.entry_type}
                        radioBtnList={ENTRY_TYPE_OPTIONS}
                        selected={formik.values.entry_type}
                    />
                </RadioGroup>
                {showFieldError('entry_type') && formik.errors.entry_type ? (
                    <FieldError>{formik.errors.entry_type}</FieldError>
                ) : null}
            </FieldBlock>

            {formik.values.entry_type === 'free' ? (
                <InsetPanel>
                    <FieldLabel>Leads permitidos *</FieldLabel>
                    <HelperText>Número entero mínimo 1. Solo aplica con tipo Free.</HelperText>
                    <TextField
                        dataId="product-assignment-allowed-leads"
                        name="allowed_leads"
                        label="Cantidad de leads"
                        originType="pcom"
                        placeHolder="Ej. 100"
                        innerLabel
                        type="number"
                        value={formik.values.allowed_leads}
                        onChange={(_, value) => formik.setFieldValue('allowed_leads', value, false)}
                        error={showFieldError('allowed_leads')}
                        errorMessage={formik.errors.allowed_leads ? `${formik.errors.allowed_leads}` : ''}
                        onBlur={() => formik.handleBlur('allowed_leads')}
                        onWheel={(e) => e.preventDefault()}
                    />
                </InsetPanel>
            ) : null}
        </FormPanel>
    );
}

const ProductAssignmentForm = () => {
    const {
        formik,
        showFieldError,
        handleDurationPresetChange,
        handleDateStartChange,
        handleDateEndChange,
        handleEntryTypeChange,
        resultModal,
        closeResultModal,
    } = useProductAssignmentForm();

    return (
        <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={es}>
            <ProductAssignmentResultModal state={resultModal} onClose={closeResultModal} />
            <form onSubmit={formik.handleSubmit} noValidate>
                <FormStack>
                    <ProductEmailPanel formik={formik} showFieldError={showFieldError} />
                    <VigenciaPanel
                        formik={formik}
                        showFieldError={showFieldError}
                        handleDurationPresetChange={handleDurationPresetChange}
                        handleDateStartChange={handleDateStartChange}
                        handleDateEndChange={handleDateEndChange}
                    />
                    <ProductEntryRulesPanel
                        formik={formik}
                        showFieldError={showFieldError}
                        handleEntryTypeChange={handleEntryTypeChange}
                    />
                </FormStack>

                <FormActions>
                    <Button
                        dataId="product-assignment-submit"
                        originType="pcom"
                        size="large"
                        type="submit"
                        disabled={formik.isSubmitting}
                    >
                        {formik.isSubmitting ? 'Enviando…' : 'Añadir usuario'}
                    </Button>
                </FormActions>
            </form>
        </LocalizationProvider>
    );
};

export default ProductAssignmentForm;
